import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import LazyImage from "../ui/LazyImage";
import { createBlurDataURL } from "../../utils/image.utils";
import type { OrganizationPageProduct as BusinessPageProduct } from "../../presentation/organizations/organizationPage.types";
import whatsappIcon from "../../assets/images/icon-whatsapp.png";

interface ProductCardProps {
  product: BusinessPageProduct;
  fallbackImage: string;
  previewPrice?: string;
  viewLabel: string;
  actionLabel: string;
  onAction: (product: BusinessPageProduct) => void;
  onPrimaryAction?: (product: BusinessPageProduct) => void;
  variant?: "catalog" | "featured";
  whatsappAvailable?: boolean;
  primaryActionOpensWhatsApp?: boolean;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, fallbackImage, previewPrice, viewLabel, actionLabel, onAction, onPrimaryAction, variant = "catalog", whatsappAvailable = true, primaryActionOpensWhatsApp = false }) => variant === "featured" ? (
  <Card data-product-id={product.id} className="flex h-full cursor-pointer flex-col border-2 border-transparent bg-white/90 transition-all duration-300 hover:scale-[1.03] hover:border-primary/40 hover:shadow-2xl" onClick={() => onAction(product)}>
    <CardHeader className="flex flex-col items-center justify-center pb-2">
      <div className="mb-2 h-32 w-32 overflow-hidden rounded-xl border bg-white shadow">
        <LazyImage src={Array.isArray(product.image) ? product.image[0] : product.image} alt={product.name} width="100%" height="100%" className="h-full w-full" placeholder={createBlurDataURL(8, 8)} fallbackSrc={fallbackImage} objectFit="cover" />
      </div>
      <CardTitle className="text-center text-lg font-bold text-primary/90">{product.name}</CardTitle>
    </CardHeader>
    <CardContent className="flex flex-1 flex-col justify-between">
      <div className="mb-3 min-h-[48px] text-center text-base font-medium text-gray-600">{product.description}</div>
      <div className="mt-2 text-center text-xl font-bold text-primary">{previewPrice}</div>
      {whatsappAvailable && <button type="button" aria-label={actionLabel} onClick={(event) => { event.stopPropagation(); (onPrimaryAction ?? onAction)(product); }} className={`mt-4 inline-flex items-center justify-center gap-2 rounded-lg px-4 py-3 font-semibold text-white shadow transition hover:opacity-95 ${primaryActionOpensWhatsApp ? "bg-whatsapp hover:bg-whatsapp-dark" : "bg-primary"}`}>{primaryActionOpensWhatsApp && <img src={whatsappIcon} alt="WhatsApp" className="h-5 w-5" />}{actionLabel}</button>}
    </CardContent>
  </Card>
) : (
  <Card data-product-id={product.id} className="group cursor-pointer overflow-hidden border border-gray-200 transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-xl" onClick={() => onAction(product)}>
    <div className="relative flex h-48 items-center justify-center overflow-hidden bg-white">
      <LazyImage src={Array.isArray(product.image) ? product.image[0] : product.image} alt={product.name} width="100%" height="100%" className="h-full w-full" placeholder={createBlurDataURL(16, 16)} fallbackSrc={fallbackImage} objectFit="contain" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 transition-all duration-300 group-hover:opacity-100" />
      <button type="button" onClick={(event) => { event.stopPropagation(); onAction(product); }} className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-lg bg-white/90 px-3 py-1 font-semibold text-primary opacity-0 shadow transition-opacity duration-200 group-hover:opacity-100">{viewLabel}</button>
    </div>
    <CardContent className="p-6">
      <div className="space-y-3">
        <h3 className="min-h-14 break-words text-xl font-bold leading-7 text-gray-800">{product.name}</h3>
        <p className="text-lg font-bold text-primary">{previewPrice}</p>
        <p className="line-clamp-2 text-gray-600">{product.description}</p>
      </div>
      <div className="mt-4 flex items-center justify-center">
        <button type="button" onClick={(event) => { event.stopPropagation(); onAction(product); }} className="inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2 text-white shadow transition hover:opacity-95">{actionLabel}</button>
      </div>
    </CardContent>
  </Card>
);

export default React.memo(ProductCard);
